import React, { useCallback } from 'react'
import Zoom from 'react-medium-image-zoom'
import 'react-medium-image-zoom/dist/styles.css'
import tw, { css, TwStyle } from 'twin.macro'
import { graphql } from 'gatsby'
import Link from '@/components/link-component'
import { isPrint } from '@/utils/print'
import ImageContentful, { ImageContentfulProps } from './image-contentful'

export const fragment = graphql`
  fragment ComponentImage on ContentfulComponentImage {
    __typename
    contentful_id
    image {
      ...Asset
    }
    link {
      url
    }
    showCaption
    zoomable
    hideInPrintView
  }
`

export type ImageProps = {
  image: ImageContentfulProps
  link?: {
    url: string
  }
  showCaption?: boolean
  zoomable?: boolean
  css?: TwStyle | TwStyle[]
  loading?: 'lazy' | 'eager'
  hideInPrintView?: boolean
}

export default function Image({
  image,
  link = null,
  showCaption = false,
  zoomable = false,
  hideInPrintView = false,
  ...other
}: ImageProps): JSX.Element {
  const Wrapper = useCallback(({ children, hidden }) => {
    if (hidden) return null
    if (link?.url) return <Link to={link.url}>{children}</Link>
    if (zoomable) return <Zoom ZoomContent={ZoomContent}>{children}</Zoom>
    return children
  }, [])

  const ZoomContent = useCallback(
    ({ buttonUnzoom, modalState, img }) => (
      <>
        <div
          tw="z-20 opacity-50 transition-transform"
          css={[
            modalState !== 'LOADED' && tw`-translate-y-16`,
            modalState === 'LOADED' && tw`translate-y-0`,
            css`
              button {
                ${tw`text-white bg-indigoBlue`}
              }
            `,
          ]}
        >
          {buttonUnzoom}
        </div>
        <figure>
          {img}
          {showCaption && (
            <figcaption
              tw="fixed right-0 left-0 p-2 flex justify-center bg-gradient-to-t from-primaryBlue to-indigoBlue text-white text-xl text-center pt-1 italic transition-[bottom] opacity-70"
              css={[modalState !== 'LOADED' && tw`-bottom-16`, modalState === 'LOADED' && tw`bottom-0`]}
            >
              <cite>{image.title}</cite>
              {image.title && image.description && <> &#8212; </>}
              {image.description}
            </figcaption>
          )}
        </figure>
      </>
    ),
    []
  )

  return (
    <Wrapper hidden={hideInPrintView && isPrint()}>
      <figure tw="break-inside-avoid mt-2 mb-6 [&:not(:has(svg))]:(w-fit)">
        <ImageContentful {...other} {...image} />
        {showCaption && image && (
          <figcaption tw="w-fit max-w-sm mx-auto text-center text-xs text-gray-400 mt-2 italic">
            <cite>{image.title}</cite>
            {image.title && image.description && <> &#8212; </>}
            {image.description}
          </figcaption>
        )}
      </figure>
    </Wrapper>
  )
}
